import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../axiosConfig';

// Tab options
const TABS = [
  { id: 'membership', label: 'Membership Info' },
  { id: 'team', label: 'Team' },
  { id: 'billing', label: 'Billing' },
];

// Month options for season selects
const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

// Team Section Component
function TeamSection({ clubData }) {
  const [admins, setAdmins] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canInvite, setCanInvite] = useState(false);
  const [maxAdmins, setMaxAdmins] = useState(3);
  const [currentCount, setCurrentCount] = useState(0);

  // Invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Fetch admins
  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('myclub/admins/');
      setAdmins(response.data.admins || []);
      setPendingInvitations(response.data.pending_invitations || []);
      setCanInvite(response.data.can_invite || false);
      setMaxAdmins(response.data.max_admins || 3);
      setCurrentCount(response.data.current_count || 0);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setInviting(true);
    try {
      const response = await axiosInstance.post('myclub/admins/invite/', {
        email: inviteEmail.trim().toLowerCase()
      });
      setInviteSuccess(response.data.message || 'Invitation sent successfully!');
      setInviteEmail('');
      fetchAdmins(); // Refresh the list
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess('');
      }, 2000);
    } catch (error) {
      setInviteError(error.response?.data?.error || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) return;

    try {
      await axiosInstance.delete(`myclub/admins/invitation/${invitationId}/revoke/`);
      fetchAdmins();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to revoke invitation');
    }
  };

  const handleRemoveAdmin = async (adminId, adminName) => {
    if (!window.confirm(`Are you sure you want to remove ${adminName} as an admin?`)) return;

    try {
      await axiosInstance.delete(`myclub/admins/${adminId}/remove/`);
      fetchAdmins();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to remove admin');
    }
  };

  const formatDate = (dateString) => {
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="team-section">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-section">
      <div className="team-header">
        <div>
          <div className="team-title-row">
            <h3>Team Members</h3>
            <div className="team-info-tooltip">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
              <span className="tooltip-text">Only the owner can remove team members or revoke invitations</span>
            </div>
          </div>
          <p className="team-subtitle">
            {currentCount} of {maxAdmins} admin slots used
          </p>
        </div>
        {canInvite && (
          <button
            className="invite-btn"
            onClick={() => setShowInviteModal(true)}
          >
            + Invite Admin
          </button>
        )}
      </div>

      {/* Current Admins */}
      <div className="team-list">
        {admins.length === 0 ? (
          <div className="team-empty">
            <p>No team members yet. Invite your first admin to get started.</p>
          </div>
        ) : (
          admins.map((admin) => {
            const hasName = admin.first_name && admin.last_name;
            return (
            <div key={admin.id} className="team-member">
              <div className="member-info">
                <div className="member-name">
                  {hasName ? `${admin.first_name} ${admin.last_name}` : admin.email}
                  {admin.role === 'owner' && (
                    <span className="role-badge owner">Owner</span>
                  )}
                  {admin.role === 'admin' && (
                    <span className="role-badge admin">Admin</span>
                  )}
                </div>
                {hasName && <div className="member-email">{admin.email}</div>}
              </div>
              {admin.role !== 'owner' && (
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveAdmin(admin.id, admin.email)}
                  title="Remove admin"
                >
                  Remove
                </button>
              )}
            </div>
          )})
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div className="pending-invitations">
          <h4>Pending Invitations</h4>
          <div className="team-list">
            {pendingInvitations.map((inv) => (
              <div key={inv.id} className="team-member">
                <div className="member-info">
                  <div className="member-name">
                    {inv.email}
                    <span className="role-badge admin">Admin</span>
                    <span className="role-badge pending">Pending</span>
                  </div>
                  <div className="member-email">Expires {formatDate(inv.expires_at)}</div>
                </div>
                <button
                  className="remove-btn"
                  onClick={() => handleRevokeInvitation(inv.id)}
                  title="Revoke invitation"
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="invite-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite Team Member</h3>
              <button
                className="modal-close"
                onClick={() => setShowInviteModal(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <form onSubmit={handleInvite}>
              <div className="modal-body">
                <p className="modal-description">
                  Invite a team member to help manage your club's dashboard.
                  They'll receive an email with instructions to create an account or log in.
                </p>

                {inviteError && (
                  <div className="form-message error">{inviteError}</div>
                )}
                {inviteSuccess && (
                  <div className="form-message success">{inviteSuccess}</div>
                )}

                <div className="form-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@club.com"
                    disabled={inviting}
                    autoFocus
                  />
                </div>

                <p className="modal-hint">
                  {maxAdmins - currentCount} invitation slot{maxAdmins - currentCount !== 1 ? 's' : ''} remaining
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={inviting || !inviteEmail.trim()}
                >
                  {inviting ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Billing Section Component
function BillingSection({ clubData }) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('myclub/subscription/');
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleUpgrade = async () => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post('myclub/subscription/checkout/', {
        test_mode: false // TODO: Remove for production
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start checkout. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setActionLoading(true);
    try {
      const response = await axiosInstance.post('myclub/subscription/portal/', {
        test_mode: false // TODO: Remove for production
      });
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to open billing portal. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { month: 'long', day: 'numeric', year: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="billing-section">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading billing information...</p>
        </div>
      </div>
    );
  }

  const isSubscribed = subscription?.is_subscribed;
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end;

  return (
    <div className="billing-section">
      {isSubscribed ? (
        <>
          <div className="billing-status">
            <div className={`billing-status-badge ${cancelAtPeriodEnd ? 'canceling' : 'active'}`}>
              {cancelAtPeriodEnd ? 'Canceling' : 'Pro Plan'}
            </div>
            {cancelAtPeriodEnd ? (
              <p className="billing-description billing-cancel-notice">
                Your subscription will end on {formatDate(subscription.current_period_end)}.
                You can reactivate anytime before then.
              </p>
            ) : (
              <p className="billing-description">
                You have unlimited access to discover and connect with prospects.
              </p>
            )}
          </div>

          <div className="billing-details">
            <div className="billing-row">
              <span className="billing-label">Status</span>
              <span className={`billing-value ${cancelAtPeriodEnd ? 'billing-status-canceling' : ''}`}>
                {cancelAtPeriodEnd ? 'Cancels at period end' : 'Active'}
              </span>
            </div>
            <div className="billing-row">
              <span className="billing-label">Amount</span>
              <span className="billing-value">${subscription.billing_amount}/month</span>
            </div>
            {subscription.current_period_end && (
              <div className="billing-row">
                <span className="billing-label">
                  {cancelAtPeriodEnd ? 'Access until' : 'Next billing date'}
                </span>
                <span className="billing-value">{formatDate(subscription.current_period_end)}</span>
              </div>
            )}
          </div>

          <button
            className="billing-manage-btn"
            onClick={handleManageBilling}
            disabled={actionLoading}
          >
            {actionLoading ? 'Loading...' : cancelAtPeriodEnd ? 'Reactivate Subscription' : 'Manage Billing'}
          </button>
        </>
      ) : (
        <>
          <div className="billing-status">
            <div className="billing-status-badge free">Free Plan</div>
            <p className="billing-description">
              You're on the free plan with {subscription?.requests_remaining || 0} of {subscription?.free_request_limit || 5} discovery requests remaining.
            </p>
          </div>

          <div className="billing-upgrade-card">
            <h4>Upgrade to Pro</h4>
            <p className="billing-upgrade-price">$500<span>/month</span></p>
            <ul className="billing-upgrade-features">
              <li>Unlimited discovery requests</li>
              <li>Connect with prospects in your area</li>
              <li>Priority support</li>
            </ul>
            <button
              className="billing-upgrade-btn"
              onClick={handleUpgrade}
              disabled={actionLoading}
            >
              {actionLoading ? 'Loading...' : 'Upgrade to Pro'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function MyClubSection({ clubData }) {
  const [activeTab, setActiveTab] = useState('membership');
  const [formData, setFormData] = useState({
    // Contact Info
    contact_name: '',
    contact_email: '',
    contact_position: '',

    // Club Info
    club_website: '',
    invite_only: null,

    // Ownership
    member_owned: null,
    parent_company: '',
    true_equity_club: null,

    // Initiation Refund
    initiation_refundable: null,
    initiation_refund_percent: '',

    // Golf Season
    golf_season_year_round: null,
    golf_season_start: '',
    golf_season_end: '',

    // Waitlist - Full Golf
    waitlist_full_golf: null,
    waitlist_full_golf_months: '',

    // Waitlist - Young Executive
    waitlist_young_exec: null,
    waitlist_young_exec_months: '',

    // Waitlist - Social
    waitlist_social: null,
    waitlist_social_months: '',

    // Financial - Full Golf
    full_golf_initiation: '',
    full_golf_annual_dues: '',

    // Financial - Young Executive
    young_exec_initiation: '',
    young_exec_annual_dues: '',
    young_exec_alias: '',
    young_exec_max_age: '',

    // Financial - Social
    social_initiation: '',
    social_annual_dues: '',
    social_includes_all_non_golf: null,
    social_description: '',

    // Financial - National
    offers_national_membership: null,
    national_initiation: '',
    national_annual_dues: '',
    national_membership_miles: '',
    national_membership_alias: '',

    // Additional Charges
    annual_capital_charge: '',
    fb_obligation_monthly: '',

    // Notes
    club_notes: '',

    // Social Media (Optional)
    instagram_handle: '',
    x_handle: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasExistingData, setHasExistingData] = useState(false);

  // Membership packet state
  const [membershipPacketUrl, setMembershipPacketUrl] = useState(null);
  const [uploadingPacket, setUploadingPacket] = useState(false);
  const [packetMessage, setPacketMessage] = useState({ type: '', text: '' });
  const [showPdfModal, setShowPdfModal] = useState(false);

  // IQ Edge state
  const [iqEdgeStatus, setIqEdgeStatus] = useState(null);
  const [iqEdgeSubmitting, setIqEdgeSubmitting] = useState(false);

  // Fetch existing membership info
  const fetchMembershipInfo = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('myclub/membership-info/');
      if (response.data && response.data.membership_info) {
        const info = response.data.membership_info;
        setFormData(prev => ({
          ...prev,
          ...info,
          // Ensure null values are handled properly for selects
          golf_season_year_round: info.golf_season_year_round,
          golf_season_start: info.golf_season_start || '',
          golf_season_end: info.golf_season_end || '',
          initiation_refund_percent: info.initiation_refund_percent || '',
          waitlist_full_golf_months: info.waitlist_full_golf_months || '',
          waitlist_young_exec_months: info.waitlist_young_exec_months || '',
          waitlist_social_months: info.waitlist_social_months || '',
          full_golf_initiation: info.full_golf_initiation || '',
          full_golf_annual_dues: info.full_golf_annual_dues || '',
          young_exec_initiation: info.young_exec_initiation || '',
          young_exec_annual_dues: info.young_exec_annual_dues || '',
          young_exec_alias: info.young_exec_alias || '',
          young_exec_max_age: info.young_exec_max_age || '',
          social_initiation: info.social_initiation || '',
          social_annual_dues: info.social_annual_dues || '',
          social_description: info.social_description || '',
          national_initiation: info.national_initiation || '',
          national_annual_dues: info.national_annual_dues || '',
          national_membership_miles: info.national_membership_miles || '',
          national_membership_alias: info.national_membership_alias || '',
          annual_capital_charge: info.annual_capital_charge || '',
          fb_obligation_monthly: info.fb_obligation_monthly || '',
          club_website: info.club_website || '',
          instagram_handle: info.instagram_handle || '',
          x_handle: info.x_handle || '',
        }));
        setMembershipPacketUrl(info.membership_packet_url || null);
        setHasExistingData(true);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch membership info:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembershipInfo();
  }, [fetchMembershipInfo]);

  // Fetch IQ Edge status
  const fetchIqEdgeStatus = useCallback(async () => {
    try {
      const res = await axiosInstance.get('myclub/iq-edge/status/');
      setIqEdgeStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch IQ Edge status:', err);
    }
  }, []);

  useEffect(() => {
    fetchIqEdgeStatus();
  }, [fetchIqEdgeStatus]);

  const handleSubmitIQEdge = async () => {
    setIqEdgeSubmitting(true);
    try {
      await axiosInstance.post('myclub/iq-edge/submit/');
      await fetchIqEdgeStatus();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to submit. Please try again.';
      const missingFields = err.response?.data?.missing_fields;
      setMessage({
        type: 'error',
        text: missingFields
          ? `${errorMsg} Missing: ${missingFields.join(', ')}`
          : errorMsg,
      });
    } finally {
      setIqEdgeSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      // Decision tree logic - clear dependent fields when parent changes
      if (field === 'member_owned') {
        if (value === true) {
          updated.parent_company = '';
        } else if (value === false) {
          updated.true_equity_club = null;
        }
      }

      if (field === 'golf_season_year_round' && value === true) {
        updated.golf_season_start = '';
        updated.golf_season_end = '';
      }

      if (field === 'initiation_refundable' && value === false) {
        updated.initiation_refund_percent = '';
      }

      if (field === 'waitlist_full_golf' && value === false) {
        updated.waitlist_full_golf_months = '';
      }

      if (field === 'waitlist_young_exec' && value === false) {
        updated.waitlist_young_exec_months = '';
      }

      if (field === 'waitlist_social' && value === false) {
        updated.waitlist_social_months = '';
      }

      if (field === 'offers_national_membership' && value === false) {
        updated.national_initiation = '';
        updated.national_annual_dues = '';
        updated.national_membership_miles = '';
        updated.national_membership_alias = '';
      }

      if (field === 'social_includes_all_non_golf' && value === true) {
        updated.social_description = '';
      }

      return updated;
    });

    // Clear any existing message when user makes changes
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Prepare data - convert empty strings to null for numeric fields
      const dataToSend = {
        ...formData,
        golf_season_start: formData.golf_season_start || null,
        golf_season_end: formData.golf_season_end || null,
        initiation_refund_percent: formData.initiation_refund_percent || null,
        waitlist_full_golf_months: formData.waitlist_full_golf_months || null,
        waitlist_young_exec_months: formData.waitlist_young_exec_months || null,
        waitlist_social_months: formData.waitlist_social_months || null,
        full_golf_initiation: formData.full_golf_initiation || null,
        full_golf_annual_dues: formData.full_golf_annual_dues || null,
        young_exec_initiation: formData.young_exec_initiation || null,
        young_exec_annual_dues: formData.young_exec_annual_dues || null,
        social_initiation: formData.social_initiation || null,
        social_annual_dues: formData.social_annual_dues || null,
        national_initiation: formData.national_initiation || null,
        national_annual_dues: formData.national_annual_dues || null,
      };

      await axiosInstance.post('myclub/membership-info/', dataToSend);
      setMessage({ type: 'success', text: 'Club information saved successfully!' });
      setHasExistingData(true);
      fetchIqEdgeStatus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle membership packet upload
  const handlePacketUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setPacketMessage({ type: 'error', text: 'Only PDF files are allowed.' });
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setPacketMessage({ type: 'error', text: 'File size exceeds 50MB limit.' });
      return;
    }

    setUploadingPacket(true);
    setPacketMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('membership_packet', file);

      const response = await axiosInstance.post(
        'myclub/membership-info/upload-packet/',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setMembershipPacketUrl(response.data.membership_packet_url);
      setPacketMessage({ type: 'success', text: 'Membership packet uploaded successfully!' });
    } catch (error) {
      setPacketMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to upload. Please try again.'
      });
    } finally {
      setUploadingPacket(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Handle membership packet removal
  const handlePacketRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the membership packet?')) return;

    setUploadingPacket(true);
    setPacketMessage({ type: '', text: '' });

    try {
      await axiosInstance.delete('myclub/membership-info/upload-packet/');
      setMembershipPacketUrl(null);
      setPacketMessage({ type: 'success', text: 'Membership packet removed.' });
    } catch (error) {
      setPacketMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to remove. Please try again.'
      });
    } finally {
      setUploadingPacket(false);
    }
  };

  // Boolean select component
  const BooleanSelect = ({ label, value, onChange, helpText }) => (
    <div className="form-field">
      <label>{label}</label>
      <div className="boolean-select">
        <button
          type="button"
          className={`bool-btn ${value === true ? 'active' : ''}`}
          onClick={() => onChange(true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={`bool-btn ${value === false ? 'active' : ''}`}
          onClick={() => onChange(false)}
        >
          No
        </button>
      </div>
      {helpText && <span className="help-text">{helpText}</span>}
    </div>
  );

  if (loading) {
    return (
      <div className="content-section">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading club information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section myclub-section">
      <div className="section-header">
        <h1>My Club</h1>
      </div>

      {/* Tab Navigation */}
      <div className="myclub-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`myclub-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Membership Info Tab */}
      {activeTab === 'membership' && (
        <>
          {/* IQ Edge Status Banner */}
          {iqEdgeStatus && (
            <div style={{ marginBottom: '1.25rem' }}>
              {iqEdgeStatus.status === 'approved' && iqEdgeStatus.is_active && (
                <div style={{ background: '#f8faf9', border: '1px solid #d1d5db', borderRadius: '10px', padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#059669' }}>IQ Edge Active</span>
                    </div>
                    <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                      Expires {new Date(iqEdgeStatus.expires_at).toLocaleDateString()} ({iqEdgeStatus.days_until_expiry} days remaining)
                    </span>
                  </div>
                </div>
              )}

              {iqEdgeStatus.status === 'pending_review' && (
                <div style={{ background: '#f8faf9', border: '1px solid #d1d5db', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#d97706' }}>IQ Edge Under Review</span>
                  </div>
                  <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>Your submission is being reviewed. We'll notify you once it's approved.</span>
                </div>
              )}

              {iqEdgeStatus.status === 'rejected' && (
                <div style={{ background: '#f8faf9', border: '1px solid #d1d5db', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#dc2626' }}>IQ Edge Rejected</span>
                      </div>
                      {iqEdgeStatus.rejection_reason && (
                        <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>{iqEdgeStatus.rejection_reason}</span>
                      )}
                    </div>
                    <button
                      onClick={handleSubmitIQEdge}
                      disabled={iqEdgeSubmitting || !iqEdgeStatus.membership_complete}
                      style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1.25rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '1rem' }}
                    >
                      {iqEdgeSubmitting ? 'Submitting...' : 'Resubmit'}
                    </button>
                  </div>
                </div>
              )}

              {iqEdgeStatus.status === 'expired' && (
                <div style={{ background: '#f8faf9', border: '1px solid #d1d5db', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#d97706' }}>IQ Edge Expired</span>
                      </div>
                      <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>Re-verify your information and resubmit to regain access.</span>
                    </div>
                    <button
                      onClick={handleSubmitIQEdge}
                      disabled={iqEdgeSubmitting || !iqEdgeStatus.membership_complete}
                      style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1.25rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '1rem' }}
                    >
                      {iqEdgeSubmitting ? 'Submitting...' : 'Resubmit'}
                    </button>
                  </div>
                </div>
              )}

              {iqEdgeStatus.status === 'not_submitted' && iqEdgeStatus.membership_complete && (
                <div style={{ background: '#f8faf9', border: '1px solid #d1d5db', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#059669' }}>Ready for IQ Edge</span>
                      </div>
                      <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>Your membership info is complete. Submit for review by the PrivateIQ team &mdash; once approved, you'll get early access to new prospects.</span>
                    </div>
                    <button
                      onClick={handleSubmitIQEdge}
                      disabled={iqEdgeSubmitting}
                      style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1.25rem', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '1rem' }}
                    >
                      {iqEdgeSubmitting ? 'Submitting...' : 'Submit for IQ Edge'}
                    </button>
                  </div>
                </div>
              )}

              {iqEdgeStatus.status === 'not_submitted' && !iqEdgeStatus.membership_complete && (
                <div style={{ background: '#f8faf9', border: '1px solid #d1d5db', borderRadius: '10px', padding: '1.25rem 1.5rem' }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#111827' }}>IQ Edge</span>
                    </div>
                    <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: 0, lineHeight: 1.5 }}>
                      Only clubs approved for IQ Edge can reach out to prospects who signed up within the last seven days &mdash; completely free with completion of Membership Info.
                    </p>
                    <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '0.5rem 0 0 0', lineHeight: 1.4, fontStyle: 'italic' }}>
                      Note: The Discover Prospects tab houses potential members who have been on the platform for at least a week.
                    </p>
                  </div>

                  {iqEdgeStatus.missing_fields?.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '0.5rem', display: 'block' }}>Missing Fields</span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
                        {iqEdgeStatus.missing_fields.map((field, i) => (
                          <span
                            key={i}
                            style={{
                              display: 'inline-block',
                              padding: '0.2rem 0.6rem',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: '#374151',
                              background: '#f3f4f6',
                              borderRadius: '999px',
                              border: '1px solid #e5e7eb',
                            }}
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {message.text && (
            <div className={`form-message ${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="myclub-form">
        {/* Enhanced Placement Notice */}

        {/* Contact Information */}
        <div className="form-section">
          <h3>Contact Information</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Contact Name</label>
              <input
                type="text"
                value={formData.contact_name}
                onChange={(e) => handleChange('contact_name', e.target.value)}
                placeholder="John Smith"
              />
            </div>
            <div className="form-field">
              <label>Contact Email</label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                placeholder="john@club.com"
              />
            </div>
            <div className="form-field">
              <label>Position / Title</label>
              <input
                type="text"
                value={formData.contact_position}
                onChange={(e) => handleChange('contact_position', e.target.value)}
                placeholder="Membership Director"
              />
            </div>
            <div className="form-field">
              <label>Club Website</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="url"
                  value={formData.club_website}
                  onChange={(e) => handleChange('club_website', e.target.value)}
                  placeholder="https://www.yourclub.com"
                  style={{ flex: 1 }}
                />
                {formData.club_website && (
                  <a
                    href={formData.club_website.startsWith('http') ? formData.club_website : `https://${formData.club_website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '8px 12px',
                      background: '#f0f7f7',
                      border: '1px solid #b8d8d8',
                      borderRadius: '6px',
                      color: '#346566',
                      fontSize: '13px',
                      fontWeight: 500,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#e0eded'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = '#f0f7f7'; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    Visit
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Social Media (Optional) */}
        <div className="form-section">
          <h3>Social Media <span style={{ fontWeight: 400, fontSize: '0.8125rem', color: '#9ca3af' }}>(Optional)</span></h3>
          <div className="form-grid">
            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ color: '#346566' }}>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" fill="currentColor"/>
                </svg>
                Instagram
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px', pointerEvents: 'none' }}>@</span>
                <input
                  type="text"
                  value={formData.instagram_handle}
                  onChange={(e) => handleChange('instagram_handle', e.target.value.replace(/^@/, ''))}
                  placeholder="yourclub"
                  style={{ paddingLeft: '28px' }}
                />
              </div>
            </div>
            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: '#346566' }}>
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
                </svg>
                X (Twitter)
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px', pointerEvents: 'none' }}>@</span>
                <input
                  type="text"
                  value={formData.x_handle}
                  onChange={(e) => handleChange('x_handle', e.target.value.replace(/^@/, ''))}
                  placeholder="yourclub"
                  style={{ paddingLeft: '28px' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Club Information */}
        <div className="form-section">
          <h3>Club Information</h3>
          <BooleanSelect
            label="Is your club invite-only?"
            value={formData.invite_only}
            onChange={(val) => handleChange('invite_only', val)}
            helpText="Do prospective members need a sponsor or invitation to apply?"
          />

          {/* Membership Packet Upload */}
          <div className="form-field membership-packet-field">
            <label>Membership Packet</label>
            <p className="field-description">
              Upload your club's membership packet PDF to share with prospective members.
            </p>

            {packetMessage.text && (
              <div className={`packet-message ${packetMessage.type}`}>
                {packetMessage.text}
              </div>
            )}

            {membershipPacketUrl ? (
              <div className="packet-current">
                <div className="packet-info">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                  <span>Membership Packet (PDF)</span>
                </div>
                <div className="packet-actions">
                  <button
                    type="button"
                    className="packet-btn view"
                    onClick={() => setShowPdfModal(true)}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="packet-btn remove"
                    onClick={handlePacketRemove}
                    disabled={uploadingPacket}
                  >
                    {uploadingPacket ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="packet-upload">
                <label className={`upload-area ${uploadingPacket ? 'uploading' : ''}`}>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePacketUpload}
                    disabled={uploadingPacket}
                  />
                  {uploadingPacket ? (
                    <>
                      <div className="upload-spinner"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span>Click to upload PDF</span>
                      <span className="upload-hint">Max file size: 50MB</span>
                    </>
                  )}
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Ownership Structure */}
        <div className="form-section">
          <h3>Ownership Structure</h3>
          <BooleanSelect
            label="Is your club member-owned?"
            value={formData.member_owned}
            onChange={(val) => handleChange('member_owned', val)}
          />

          {formData.member_owned === false && (
            <div className="form-field conditional-field">
              <label>Parent Company</label>
              <input
                type="text"
                value={formData.parent_company}
                onChange={(e) => handleChange('parent_company', e.target.value)}
                placeholder="e.g., Invited, Troon, Concert Golf Partners"
              />
            </div>
          )}

          {formData.member_owned === true && (
            <div className="conditional-field">
              <BooleanSelect
                label="Is this a true equity club?"
                value={formData.true_equity_club}
                onChange={(val) => handleChange('true_equity_club', val)}
                helpText="Would members receive proceeds proportional to ownership if the club were sold?"
              />
            </div>
          )}
        </div>

        {/* Initiation Refund */}
        <div className="form-section">
          <h3>Initiation Fee Refund Policy</h3>
          <BooleanSelect
            label="Is the initiation fee refundable?"
            value={formData.initiation_refundable}
            onChange={(val) => handleChange('initiation_refundable', val)}
          />

          {formData.initiation_refundable === true && (
            <div className="form-field conditional-field">
              <label>Refund Percentage</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.initiation_refund_percent}
                  onChange={(e) => handleChange('initiation_refund_percent', e.target.value)}
                  placeholder="e.g., 80"
                />
                <span className="suffix">%</span>
              </div>
              <span className="help-text">Percentage returned upon membership replacement</span>
            </div>
          )}
        </div>

        {/* Golf Season */}
        <div className="form-section">
          <h3>Golf Season</h3>
          <BooleanSelect
            label="Is your golf season year-round?"
            value={formData.golf_season_year_round}
            onChange={(val) => handleChange('golf_season_year_round', val)}
          />
          {formData.golf_season_year_round === false && (
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Season Start</label>
                <select
                  value={formData.golf_season_start}
                  onChange={(e) => handleChange('golf_season_start', e.target.value ? parseInt(e.target.value) : '')}
                >
                  <option value="">Select month</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Season End</label>
                <select
                  value={formData.golf_season_end}
                  onChange={(e) => handleChange('golf_season_end', e.target.value ? parseInt(e.target.value) : '')}
                >
                  <option value="">Select month</option>
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Waitlist Information */}
        <div className="form-section">
          <h3>Waitlist Information</h3>

          {/* Full Golf Membership */}
          <div className="waitlist-group">
            <BooleanSelect
              label="Is there a waitlist for Full Golf membership?"
              value={formData.waitlist_full_golf}
              onChange={(val) => handleChange('waitlist_full_golf', val)}
            />
            {formData.waitlist_full_golf === true && (
              <div className="form-field conditional-field inline-field">
                <label>Estimated wait time</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    value={formData.waitlist_full_golf_months}
                    onChange={(e) => handleChange('waitlist_full_golf_months', e.target.value)}
                    placeholder="0"
                  />
                  <span className="suffix">months</span>
                </div>
              </div>
            )}
          </div>

          {/* Young Executive Membership */}
          <div className="waitlist-group">
            <BooleanSelect
              label="Is there a waitlist for Young Executive membership?"
              value={formData.waitlist_young_exec}
              onChange={(val) => handleChange('waitlist_young_exec', val)}
            />
            {formData.waitlist_young_exec === true && (
              <div className="form-field conditional-field inline-field">
                <label>Estimated wait time</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    value={formData.waitlist_young_exec_months}
                    onChange={(e) => handleChange('waitlist_young_exec_months', e.target.value)}
                    placeholder="0"
                  />
                  <span className="suffix">months</span>
                </div>
              </div>
            )}
          </div>

          {/* Social Membership */}
          <div className="waitlist-group">
            <BooleanSelect
              label="Is there a waitlist for Social membership?"
              value={formData.waitlist_social}
              onChange={(val) => handleChange('waitlist_social', val)}
            />
            {formData.waitlist_social === true && (
              <div className="form-field conditional-field inline-field">
                <label>Estimated wait time</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    value={formData.waitlist_social_months}
                    onChange={(e) => handleChange('waitlist_social_months', e.target.value)}
                    placeholder="0"
                  />
                  <span className="suffix">months</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Membership Pricing */}
        <div className="form-section">
          <h3>Membership Pricing</h3>
          {/* Full Golf */}
          <div className="pricing-group">
            <h4>Full Golf Membership</h4>
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Initiation Fee</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.full_golf_initiation}
                    onChange={(e) => handleChange('full_golf_initiation', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Annual Dues</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.full_golf_annual_dues}
                    onChange={(e) => handleChange('full_golf_annual_dues', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Young Executive */}
          <div className="pricing-group">
            <h4>Young Executive Membership</h4>
            <div className="form-grid two-col" style={{ marginBottom: '12px' }}>
              <div className="form-field">
                <label>Also Known As</label>
                <input
                  type="text"
                  value={formData.young_exec_alias}
                  onChange={(e) => handleChange('young_exec_alias', e.target.value)}
                  placeholder="e.g., Junior Membership"
                />
              </div>
              <div className="form-field">
                <label>Age Limit</label>
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    max="99"
                    value={formData.young_exec_max_age}
                    onChange={(e) => handleChange('young_exec_max_age', e.target.value)}
                    placeholder="e.g., 35"
                  />
                  <span className="suffix">& under</span>
                </div>
              </div>
            </div>
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Initiation Fee</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.young_exec_initiation}
                    onChange={(e) => handleChange('young_exec_initiation', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Annual Dues</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.young_exec_annual_dues}
                    onChange={(e) => handleChange('young_exec_annual_dues', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Social */}
          <div className="pricing-group">
            <h4>Social Membership</h4>
            <BooleanSelect
              label="Does Social Membership include all club privileges other than golf?"
              value={formData.social_includes_all_non_golf}
              onChange={(val) => handleChange('social_includes_all_non_golf', val)}
            />
            {formData.social_includes_all_non_golf === false && (
              <div className="form-field conditional-field" style={{ marginBottom: '12px' }}>
                <label>What does Social Membership include?</label>
                <textarea
                  value={formData.social_description}
                  onChange={(e) => handleChange('social_description', e.target.value)}
                  placeholder="Describe what is included with social membership..."
                  rows={3}
                />
              </div>
            )}
            <div className="form-grid two-col">
              <div className="form-field">
                <label>Initiation Fee</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.social_initiation}
                    onChange={(e) => handleChange('social_initiation', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Annual Dues</label>
                <div className="input-with-prefix">
                  <span className="prefix">$</span>
                  <input
                    type="number"
                    min="0"
                    value={formData.social_annual_dues}
                    onChange={(e) => handleChange('social_annual_dues', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* National Membership */}
          <div className="pricing-group">
            <h4>National Membership</h4>
            <BooleanSelect
              label="Do you offer a National Membership?"
              value={formData.offers_national_membership}
              onChange={(val) => handleChange('offers_national_membership', val)}
            />
            {formData.offers_national_membership === true && (
              <>
                <div className="form-grid two-col conditional-field" style={{ marginBottom: '12px' }}>
                  <div className="form-field">
                    <label>Also Known As</label>
                    <input
                      type="text"
                      value={formData.national_membership_alias}
                      onChange={(e) => handleChange('national_membership_alias', e.target.value)}
                      placeholder="e.g., Non-Resident Membership"
                    />
                  </div>
                  <div className="form-field">
                    <label>Minimum Distance from Club</label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        min="0"
                        value={formData.national_membership_miles}
                        onChange={(e) => handleChange('national_membership_miles', e.target.value)}
                        placeholder="100"
                      />
                      <span className="suffix">miles</span>
                    </div>
                  </div>
                </div>
                <div className="form-grid two-col conditional-field">
                  <div className="form-field">
                    <label>Initiation Fee</label>
                    <div className="input-with-prefix">
                      <span className="prefix">$</span>
                      <input
                        type="number"
                        min="0"
                        value={formData.national_initiation}
                        onChange={(e) => handleChange('national_initiation', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Annual Dues</label>
                    <div className="input-with-prefix">
                      <span className="prefix">$</span>
                      <input
                        type="number"
                        min="0"
                        value={formData.national_annual_dues}
                        onChange={(e) => handleChange('national_annual_dues', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Additional Charges */}
        <div className="form-section">
          <h3>Additional Charges</h3>
          <div className="form-grid two-col">
            <div className="form-field">
              <label>Annual Capital Charges</label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="number"
                  min="0"
                  value={formData.annual_capital_charge}
                  onChange={(e) => handleChange('annual_capital_charge', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="form-field">
              <label>Monthly F&B Minimum</label>
              <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input
                  type="number"
                  min="0"
                  value={formData.fb_obligation_monthly}
                  onChange={(e) => handleChange('fb_obligation_monthly', e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="form-section">
          <h3>Additional Notes</h3>
          <div className="form-field">
            <label>Notes for Prospective Members</label>
            <textarea
              value={formData.club_notes}
              onChange={(e) => handleChange('club_notes', e.target.value)}
              placeholder="Any additional information you'd like to share with prospective members..."
              rows={4}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={saving}>
            {saving ? 'Saving...' : hasExistingData ? 'Update Information' : 'Save Information'}
          </button>
        </div>
      </form>

          {/* PDF Viewer Modal */}
          {showPdfModal && membershipPacketUrl && (
            <div className="pdf-modal-overlay" onClick={() => setShowPdfModal(false)}>
              <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pdf-modal-header">
                  <h3>Membership Packet</h3>
                  <button
                    type="button"
                    className="pdf-modal-close"
                    onClick={() => setShowPdfModal(false)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div className="pdf-modal-body">
                  <iframe
                    src={membershipPacketUrl}
                    title="Membership Packet"
                    width="100%"
                    height="100%"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <TeamSection clubData={clubData} />
      )}

      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <BillingSection clubData={clubData} />
      )}
    </div>
  );
}

export default MyClubSection;
